import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { Mail, Lock, Building2, AlertCircle, CheckCircle } from 'lucide-react';
import RequestPartnershipModal from '@/components/landing/RequestPartnershipModal';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

const signupSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  companyName: z.string().min(1, 'Company name is required'),
});

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, signIn, signUp, loading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [partnershipModalOpen, setPartnershipModalOpen] = useState(false);

  // Check if this is an invited signup
  const invitedEmail = searchParams.get('email');
  const invitationId = searchParams.get('invitation');
  const isInvitedSignup = !!invitedEmail && !!invitationId;

  // Form mode: 'login' or 'signup' (signup only available for invited users)
  const [mode, setMode] = useState<'login' | 'signup'>(isInvitedSignup ? 'signup' : 'login');

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Signup form state (pre-filled with invited email)
  const [signupEmail, setSignupEmail] = useState(invitedEmail || '');
  const [signupPassword, setSignupPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  
  // Invitation validation state
  const [invitationValid, setInvitationValid] = useState<boolean | null>(null);
  const [checkingInvitation, setCheckingInvitation] = useState(isInvitedSignup);

  // Validate invitation on mount
  useEffect(() => {
    const validateInvitation = async () => {
      if (!invitedEmail || !invitationId) {
        setCheckingInvitation(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .rpc('validate_invitation', {
            invitation_id: invitationId,
            invitation_email: invitedEmail,
          })
          .single();

        if (error || !data) {
          setInvitationValid(false);
          setError('This invitation is invalid or has already been used.');
        } else {
          setInvitationValid(true);
          // Pre-fill company name if provided in invitation
          if (data.company_name) {
            setCompanyName(data.company_name);
          }
        }
      } catch (err) {
        setInvitationValid(false);
        setError('Failed to validate invitation.');
      } finally {
        setCheckingInvitation(false);
      }
    };

    validateInvitation();
  }, [invitedEmail, invitationId]);

  useEffect(() => {
    if (user) {
      // First-time users go to onboarding wizard; returning users go to command center
      const checkOnboarding = async () => {
        const { data } = await supabase
          .from('profiles')
          .select('wizard_completed')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!data || !data.wizard_completed) {
          navigate('/hub/dashboard');
        } else {
          navigate('/hub');
        }
      };
      checkOnboarding();
    }
  }, [user, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const validation = loginSchema.safeParse({ email: loginEmail, password: loginPassword });
      if (!validation.success) {
        setError(validation.error.errors[0].message);
        setIsLoading(false);
        return;
      }

      const { error } = await signIn(loginEmail, loginPassword);
      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          setError('Invalid email or password');
        } else if (error.message.includes('Email not confirmed')) {
          setError('Please confirm your email before signing in');
        } else if (error.message.includes('too many requests')) {
          setError('Too many login attempts. Please try again later.');
        } else {
          console.error('Authentication error:', error);
          setError('Unable to sign in at this time. Please try again later.');
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsLoading(true);

    // Ensure we have a valid invitation
    if (!invitationValid) {
      setError('You need a valid invitation to create an account.');
      setIsLoading(false);
      return;
    }

    try {
      const validation = signupSchema.safeParse({ 
        email: signupEmail, 
        password: signupPassword,
        companyName 
      });
      if (!validation.success) {
        setError(validation.error.errors[0].message);
        setIsLoading(false);
        return;
      }

      // Double-check invitation is still valid before signup
      const { data: invitations } = await supabase
        .rpc('validate_invitation', {
          invitation_id: invitationId!,
          invitation_email: signupEmail,
        });

      if (!invitations || invitations.length === 0) {
        setError('This invitation is no longer valid.');
        setIsLoading(false);
        return;
      }

      const { error } = await signUp(signupEmail, signupPassword, companyName);
      if (error) {
        if (error.message.includes('already registered')) {
          setError('This email is already registered. Please sign in instead.');
        } else if (error.message.includes('weak password')) {
          setError('Password is too weak. Use at least 8 characters with mixed case and numbers.');
        } else {
          console.error('Signup error:', error);
          setError('Unable to create account. Please try again later.');
        }
      } else {
        setSuccessMessage('Account created! Please check your email to verify your account.');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (loading || checkingInvitation) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary rounded-full border-t-transparent"></div>
      </div>
    );
  }

  return (
    <>
    <RequestPartnershipModal open={partnershipModalOpen} onOpenChange={setPartnershipModalOpen} />
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="absolute inset-0 hero-gradient pointer-events-none" />
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="relative h-10 w-10">
              <svg viewBox="0 0 100 100" className="h-full w-full">
                <defs>
                  <linearGradient id="authLogoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="hsl(190 100% 50%)" />
                    <stop offset="100%" stopColor="hsl(210 70% 50%)" />
                  </linearGradient>
                </defs>
                <circle cx="50" cy="50" r="46" stroke="url(#authLogoGrad)" strokeWidth="2" strokeDasharray="10 5" fill="none">
                  <animateTransform attributeName="transform" type="rotate" from="0 50 50" to="360 50 50" dur="20s" repeatCount="indefinite"/>
                </circle>
                <g transform="translate(10, 30) scale(0.36)">
                  <path 
                    d="M40 55 C40 25 90 25 110 55 C130 85 180 85 180 55 C180 25 130 25 110 55 C90 85 40 85 40 55 Z" 
                    fill="none" 
                    stroke="url(#authLogoGrad)" 
                    strokeWidth="18" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                  />
                </g>
              </svg>
            </div>
            <span className="text-2xl font-bold text-foreground">ClearSlot</span>
          </div>
          <p className="text-muted-foreground">Partner Hub</p>
        </div>

        <Card className="border-border/50 bg-card/80 backdrop-blur-xl">
          <CardHeader className="text-center">
            <CardTitle>
              {mode === 'login' ? 'Welcome Back' : 'Create Your Account'}
            </CardTitle>
            <CardDescription>
              {mode === 'login' 
                ? 'Sign in to access your partner dashboard' 
                : 'Complete your registration to get started'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {mode === 'login' ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email
                  </Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="you@company.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password" className="flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    Password
                  </Label>
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="••••••••"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    required
                  />
                </div>

                {error && (
                  <div className="p-3 bg-destructive/10 border border-destructive/50 rounded-md flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                    <p className="text-sm text-destructive">{error}</p>
                  </div>
                )}

                <Button type="submit" className="w-full" variant="hero" disabled={isLoading}>
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>

                {/* Invite-only message */}
                <div className="pt-4 border-t border-border/50">
                  <p className="text-center text-sm text-muted-foreground">
                    Don't have an account? The Partner Hub is invite-only.
                    <br />
                    <button type="button" onClick={() => setPartnershipModalOpen(true)} className="text-primary hover:underline">
                      Contact us to request access
                    </button>
                  </p>
                </div>
              </form>
            ) : (
              <>
                {/* Invited signup form */}
                {invitationValid === false ? (
                  <div className="text-center py-8">
                    <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">Invalid Invitation</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {error || 'This invitation link is invalid or has expired.'}
                    </p>
                    <Button variant="outline" onClick={() => { setMode('login'); setError(null); }}>
                      Go to Sign In
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSignup} className="space-y-4">
                    <div className="p-3 bg-primary/10 border border-primary/30 rounded-md flex items-start gap-2 mb-4">
                      <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <p className="text-sm text-primary">
                        You've been invited to join ClearSlot Partner Hub!
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-email" className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Email
                      </Label>
                      <Input
                        id="signup-email"
                        type="email"
                        value={signupEmail}
                        disabled
                        className="bg-muted"
                      />
                      <p className="text-xs text-muted-foreground">
                        This email was specified in your invitation
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="company-name" className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Company Name
                      </Label>
                      <Input
                        id="company-name"
                        type="text"
                        placeholder="Your Platform Name"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-password" className="flex items-center gap-2">
                        <Lock className="h-4 w-4" />
                        Password
                      </Label>
                      <Input
                        id="signup-password"
                        type="password"
                        placeholder="••••••••"
                        value={signupPassword}
                        onChange={(e) => setSignupPassword(e.target.value)}
                        required
                      />
                      <p className="text-xs text-muted-foreground">
                        Must be at least 6 characters
                      </p>
                    </div>

                    {error && (
                      <div className="p-3 bg-destructive/10 border border-destructive/50 rounded-md flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                        <p className="text-sm text-destructive">{error}</p>
                      </div>
                    )}

                    {successMessage && (
                      <div className="p-3 bg-accent/10 border border-accent/50 rounded-md flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                        <p className="text-sm text-accent">{successMessage}</p>
                      </div>
                    )}

                    <Button type="submit" className="w-full" variant="hero" disabled={isLoading}>
                      {isLoading ? 'Creating account...' : 'Create Account'}
                    </Button>

                    <p className="text-center text-sm text-muted-foreground">
                      Already have an account?{' '}
                      <button 
                        type="button"
                        onClick={() => { setMode('login'); setError(null); }}
                        className="text-primary hover:underline"
                      >
                        Sign in
                      </button>
                    </p>
                  </form>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-center mt-6 text-sm text-muted-foreground">
          <a href="/" className="hover:text-foreground transition-colors">
            ← Back to homepage
          </a>
        </p>
      </motion.div>
    </div>
    </>
  );
};

export default Auth;
